/**
 * Viewer mode context (WHA-3712).
 *
 * Lets nested viewer components (notably `ModelPicker`) tell whether they're
 * rendering inside the fullscreen preview overlay (`IfcModelOverlay`) vs the
 * regular /models page, without threading props through
 * `IfcViewerPage` → `IfcViewer` → `ModelPicker`.
 *
 * In preview mode the model picker is hidden unless the previewed file has
 * multiple versions, in which case it shows only that file's version chain.
 * The default (no provider) is the normal /models page behaviour.
 *
 * Kept in its own module so the overlay (provider) and the picker (consumer)
 * can both import it without an import cycle.
 */

import { createContext, useContext } from "react";

export interface IfcViewerMode {
  /** True when rendered inside the fullscreen `.ifc` preview overlay. */
  preview: boolean;
  /** The document being previewed (drives the versions-only picker). */
  previewDocId: number | null;
}

const IfcViewerModeContext = createContext<IfcViewerMode>({
  preview: false,
  previewDocId: null,
});

export const IfcViewerModeProvider = IfcViewerModeContext.Provider;

export function useIfcViewerMode(): IfcViewerMode {
  return useContext(IfcViewerModeContext);
}
